import * as Portal from "@radix-ui/react-portal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  type InvoiceData,
  type LineItem,
  LineItemCreatedBy,
  type TaxCode,
} from "@/apis/gql/types";
import { TotalAmountsOverview } from "@/features/invoice-standard-review/accounting/splitInvoiceForm/splitInvoiceTable/InvoiceTotalsOverview";
import { useTableInstanceContext } from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  calculateBruttoAmount,
  calculateCheckSums,
  calculateNettoAmount,
} from "@/lib/utils";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";
import { InvoiceModes } from "@/types/utils";

import {
  AddBookingForm,
  type AddLineItemFormType,
} from "@/components/shared/bookings/add-booking-form";
import { EllipsisVertical, Paperclip, Split, Unlink2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BulkEditLineItemActionBar } from "./bulk-edit-booking-bar";

type InvoiceBookingsActionBarProps = {
  invoiceData: {
    invoiceId: InvoiceData["invoiceId"];
    isNettoInvoice: InvoiceData["isNettoInvoice"];
    totalAmount: InvoiceData["totalAmount"];
    currency: InvoiceData["currency"];
  };
  invoiceMode: InvoiceModes;
  lineItems: LineItem[];
  isAddingBooking: boolean;
  onAddLineItem: (lineItem: Partial<LineItem>) => void;
  onUpdateAccountId: (
    lineItemIds: string[],
    accountId: string | null,
    vatRate: number | undefined,
    invoiceMode: InvoiceModes,
  ) => void;
  onUpdateTaxCodeId: (
    lineItemIds: string[],
    taxCodeId: string | null,
    vatRate: number,
    invoiceMode: InvoiceModes,
  ) => void;
  onUpdateCostCenterId: (
    lineItemIds: string[],
    costCenterId: string | null,
  ) => void;
  onDelete: (lineItemIds: string[]) => void;
  onUnlink: (lineItemIds: string[]) => void;
};

export const InvoiceBookingsActionBar = ({
  invoiceData,
  lineItems: _lineItems,
  invoiceMode,
  isAddingBooking,
  onAddLineItem,
  onUpdateAccountId,
  onUpdateTaxCodeId,
  onUpdateCostCenterId,
  onDelete,
  onUnlink,
}: InvoiceBookingsActionBarProps) => {
  const { t } = useTranslation();
  const [selectionType, setSelectionType] = useState<"row" | "item" | null>(
    null,
  );
  const { table } = useTableInstanceContext();
  const { taxCodes } = useGetAllTaxCodes();
  const { accounts } = useGetAllAccounts();
  const { isNettoInvoice, totalAmount } = invoiceData;

  const lineItems = useMemo(() => {
    return _lineItems.filter((lineItem) => !lineItem.deletedAt);
  }, [_lineItems]);

  const { isCorrectAmount } = calculateCheckSums(
    totalAmount,
    lineItems,
    isNettoInvoice,
  );

  useEffect(() => {
    if (table.getSelectedRowModel().flatRows.length > 0) {
      setSelectionType("row");
    } else {
      setSelectionType(null);
    }
  }, [table.getSelectedRowModel().rows]);

  const lineItemIds = table
    .getSelectedRowModel()
    .flatRows.filter((row) => row.getParentRow())
    .map((row) => row.original.id);

  const handleAddLineItem = useCallback(
    async (values: AddLineItemFormType) => {
      const {
        accountId,
        taxCodeId: _taxCodeId,
        amount,
        costCenterId,
        bookingText,
      } = values;

      const account = accounts.find(
        (account) => account.accountId === accountId,
      );
      const taxCodeId = account?.taxCodeId ?? _taxCodeId;

      const taxCode = taxCodes?.find(
        (taxCode: TaxCode) => taxCode.taxCodeId === taxCodeId,
      );

      const _vatRate = taxCode?.vatRate ?? 0;

      // TODO: fix this some day
      //@ts-ignore for a reason vatRate is a string. force to be a number
      const vatRate = Number.parseFloat(_vatRate);

      const _amount =
        invoiceMode === InvoiceModes.Brutto
          ? amount
          : calculateBruttoAmount(amount, vatRate);

      const lineItem = {
        lineItemId: uuidv4(),
        taxCodeId,
        vatRate,
        costCenterId: costCenterId ?? null,
        amount: _amount,
        nettoAmount: calculateNettoAmount(_amount, vatRate),
        accountId,
        name: bookingText ?? "",
        description: bookingText ?? "",
        bookingText: bookingText ?? "",
        invoiceId: invoiceData.invoiceId,
        createdAt: new Date().toISOString(),
        createdBy: LineItemCreatedBy.Manual,
        bookingTextManuallyUpdated: true,
      };

      onAddLineItem(lineItem);

      setTimeout(() => {
        const element = document.getElementById("invoice-details-panel");
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      }, 300);
    },
    [onAddLineItem, taxCodes, invoiceData.invoiceId],
  );

  const handleDeleteSelected = useCallback(() => {
    onDelete(lineItemIds);
  }, [onDelete, lineItemIds]);

  const handleAccountChange = useCallback(
    (value: string | null) => {
      const account = accounts.find((account) => account.accountId === value);
      const taxCodeId = account?.taxCodeId;
      const taxCode = taxCodes.find(
        (taxCode) => taxCode.taxCodeId === taxCodeId,
      );

      const vatRate =
        taxCode?.vatRate != null
          ? //@ts-ignore for a reason vatRate is a string. force to be a number
            Number.parseFloat(taxCode.vatRate)
          : undefined;

      onUpdateAccountId(lineItemIds, value, vatRate, invoiceMode);
    },
    [onUpdateAccountId, lineItemIds, taxCodes, accounts, invoiceMode],
  );

  const handleTaxCodeChange = useCallback(
    (value: string | null) => {
      const taxCode = taxCodes?.find(
        (taxCode: TaxCode) => taxCode.taxCodeId === value,
      );

      const vatRate = taxCode?.vatRate
        ? //@ts-ignore for a reason vatRate is a string. force to be a number
          Number.parseFloat(taxCode.vatRate)
        : 0;

      onUpdateTaxCodeId(lineItemIds, value, vatRate, invoiceMode);
    },
    [onUpdateTaxCodeId, lineItemIds, taxCodes, invoiceMode],
  );

  const handleCostCenterChange = useCallback(
    (value: string | null) => {
      onUpdateCostCenterId(lineItemIds, value);
    },
    [onUpdateCostCenterId, lineItemIds],
  );

  const handleUnlink = useCallback(() => {
    onUnlink(lineItemIds);
    table.resetRowSelection(true);
  }, [onUnlink, lineItemIds, table]);

  return (
    <Portal.Root container={document.getElementById("line-item-bar-portal")}>
      <div className="flex flex-col gap-2 px-5 my-2">
        {selectionType === "row" ? (
          <DropdownMenu>
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <BulkEditLineItemActionBar
                  table={table}
                  onDelete={handleDeleteSelected}
                  onAccountChange={handleAccountChange}
                  onTaxCodeChange={handleTaxCodeChange}
                  onCostCenterChange={handleCostCenterChange}
                />
              </div>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
            </div>
            <DropdownMenuContent
              className="w-72"
              align="end"
              side="top"
              sideOffset={10}
            >
              <DropdownMenuGroup>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      onSelect={handleUnlink}
                      className="cursor-pointer gap-2"
                    >
                      <Split size={16} />
                      <span>
                        {t("separate_bookings.label", {
                          count: lineItemIds.length,
                        })}
                      </span>
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent align="end">
                    {t("separate_bookings.tooltip")}
                  </TooltipContent>
                </Tooltip>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <>
            {(isAddingBooking || !isCorrectAmount) && (
              <AddBookingForm
                invoiceMode={invoiceMode}
                onSubmit={handleAddLineItem}
              />
            )}
          </>
        )}
        <TotalAmountsOverview
          totalAmount={totalAmount}
          lineItems={lineItems}
          isNettoInvoice={isNettoInvoice}
          currency={invoiceData.currency}
          invoiceMode={invoiceMode}
        />
      </div>
    </Portal.Root>
  );
};
